/**
 * LandingPage — public marketing landing, served in-app at "/".
 *
 * The markup lives in landing/landing-body.html (imported as a raw string and
 * injected here) and its styles in landing/landing.css (scoped under .htl).
 * There is no public landing.html file — nothing is exposed; the only URL is "/".
 * All interactivity (i18n toggle, scroll reveal, count-up, marquee, parallax)
 * is wired here, scoped to this component's container.
 */

import { useEffect, useRef } from 'react';
import bodyHtml from './landing/landing-body.html?raw';
import './landing/landing.css';

const I18N = {
    en: {
        title: 'Hit the Land — Trace deforestation risk back to the plot it came from',
        svChip1: 'Cocoa · Lot 0934', svChip2: '12.4 t · EU-bound', svFlag: 'Forest loss detected · 2.1 ha',
    },
    es: {
        title: 'Hit the Land — Rastrea el riesgo de deforestación hasta el lote de origen',
        svChip1: 'Cacao · Lote 0934', svChip2: '12,4 t · destino UE', svFlag: 'Pérdida forestal detectada · 2,1 ha',
    },
} as const;

const LANG_KEY = 'htl_lang';

export default function LandingPage() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = ref.current;
        if (!root) return;
        const prevTitle = document.title;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const $$ = (sel: string) => Array.from(root.querySelectorAll(sel));

        // ── i18n ──
        function setLang(l: string) {
            const lang: 'en' | 'es' = l === 'es' ? 'es' : 'en';
            const t = I18N[lang];
            root!.setAttribute('data-lang', lang);
            try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
            document.title = t.title;
            (['svChip1', 'svChip2', 'svFlag'] as const).forEach((id) => {
                const el = root!.querySelector('#' + id);
                if (el) el.textContent = t[id];
            });
            $$('[data-setlang]').forEach((b) =>
                b.classList.toggle('on', b.getAttribute('data-setlang') === lang),
            );
        }
        const urlLang = new URLSearchParams(location.search).get('lang');
        let saved: string | null = null;
        try { saved = localStorage.getItem(LANG_KEY); } catch { /* ignore */ }
        const navLang = (navigator.language || 'en').toLowerCase().indexOf('es') === 0 ? 'es' : 'en';
        setLang(urlLang || saved || navLang);
        const langHandlers: Array<[Element, () => void]> = [];
        $$('[data-setlang]').forEach((b) => {
            const h = () => setLang(b.getAttribute('data-setlang') || 'en');
            b.addEventListener('click', h);
            langHandlers.push([b, h]);
        });

        // ── year ──
        const yr = root.querySelector('#yr');
        if (yr) yr.textContent = '© ' + new Date().getFullYear();

        // ── nav shadow on scroll ──
        const nav = root.querySelector('#nav');
        const onScroll = () => { if (nav) nav.classList.toggle('scrolled', window.scrollY > 8); };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });

        // ── single-open FAQ ──
        const details = $$('.faq details') as HTMLDetailsElement[];
        const detailHandlers: Array<[HTMLDetailsElement, () => void]> = [];
        details.forEach((d) => {
            const h = () => { if (d.open) details.forEach((o) => { if (o !== d) o.open = false; }); };
            d.addEventListener('toggle', h);
            detailHandlers.push([d, h]);
        });

        // ── seamless marquee: duplicate track once ──
        $$('.marquee-track').forEach((tr) => {
            const el = tr as HTMLElement;
            if (!el.dataset.duped) { el.innerHTML += el.innerHTML; el.dataset.duped = '1'; }
        });

        const observers: IntersectionObserver[] = [];

        if (reduce) {
            $$('.reveal').forEach((el) => el.classList.add('in'));
            $$('[data-to]').forEach((el) => {
                el.textContent = (el.getAttribute('data-to') || '') + (el.getAttribute('data-suffix') || '');
            });
        } else {
            // scroll reveal
            const io = new IntersectionObserver((entries) => {
                entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
            }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });
            $$('.reveal').forEach((el) => io.observe(el));
            observers.push(io);

            // count-up
            const countUp = (el: Element) => {
                const to = parseFloat(el.getAttribute('data-to') || '0') || 0;
                const suf = el.getAttribute('data-suffix') || '';
                const dur = 1200;
                let start: number | null = null;
                const frame = (ts: number) => {
                    if (start === null) start = ts;
                    const p = Math.min((ts - start) / dur, 1);
                    const eased = 1 - Math.pow(1 - p, 3);
                    el.textContent = Math.round(to * eased) + suf;
                    if (p < 1) requestAnimationFrame(frame); else el.textContent = to + suf;
                };
                requestAnimationFrame(frame);
            };
            const sio = new IntersectionObserver((entries) => {
                entries.forEach((e) => { if (e.isIntersecting) { countUp(e.target); sio.unobserve(e.target); } });
            }, { threshold: 0.6 });
            $$('[data-to]').forEach((el) => sio.observe(el));
            observers.push(sio);

            // hero diagram mouse parallax
            const vis = root.querySelector('.hero-visual') as HTMLElement | null;
            if (vis) {
                const layers = Array.from(vis.querySelectorAll('.lyr')) as SVGElement[];
                const onMove = (e: MouseEvent) => {
                    const r = vis.getBoundingClientRect();
                    let x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
                    let y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
                    x = Math.max(-1, Math.min(1, x)); y = Math.max(-1, Math.min(1, y));
                    layers.forEach((l) => {
                        const d = (parseFloat(l.getAttribute('data-depth') || '0') || 0) / 10;
                        l.style.transform = 'translate(' + (x * d * -6).toFixed(1) + 'px,' + (y * d * -6).toFixed(1) + 'px)';
                    });
                };
                const onLeave = () => layers.forEach((l) => { l.style.transform = ''; });
                vis.addEventListener('mousemove', onMove, { passive: true });
                vis.addEventListener('mouseleave', onLeave);
                (root as HTMLElement & { _htlVis?: () => void })._htlVis = () => {
                    vis.removeEventListener('mousemove', onMove);
                    vis.removeEventListener('mouseleave', onLeave);
                };
            }
        }

        return () => {
            document.title = prevTitle;
            window.removeEventListener('scroll', onScroll);
            langHandlers.forEach(([el, h]) => el.removeEventListener('click', h));
            detailHandlers.forEach(([el, h]) => el.removeEventListener('toggle', h));
            observers.forEach((o) => o.disconnect());
            const v = (root as HTMLElement & { _htlVis?: () => void })._htlVis;
            if (v) v();
        };
    }, []);

    return <div className="htl" data-lang="en" ref={ref} dangerouslySetInnerHTML={{ __html: bodyHtml }} />;
}
